import type { UploadResponse, ConvertResponse, StatusResponse } from "@quickconv/shared";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8787";

/** Rate limit info extracted from API response headers */
export interface RateLimitInfo {
  remaining: number;
  limit: number;
}

/** Extract X-RateLimit-* headers from fetch Response */
function extractRateLimitFromFetch(res: Response): RateLimitInfo | null {
  const remaining = res.headers.get("X-RateLimit-Remaining");
  const limit = res.headers.get("X-RateLimit-Limit");
  if (remaining !== null && limit !== null) {
    return { remaining: Number(remaining), limit: Number(limit) };
  }
  return null;
}

export async function uploadFile(
  file: File,
  onProgress?: (progress: number) => void,
  onRateLimit?: (info: RateLimitInfo) => void,
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append("file", file);

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/upload`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Extract rate limit info from response headers
        const remaining = xhr.getResponseHeader("X-RateLimit-Remaining");
        const limit = xhr.getResponseHeader("X-RateLimit-Limit");
        if (remaining !== null && limit !== null && onRateLimit) {
          onRateLimit({ remaining: Number(remaining), limit: Number(limit) });
        }
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(JSON.parse(xhr.responseText).message || "Upload failed"));
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(formData);
  });
}

export async function requestConversion(
  fileId: string,
  outputFormat: string,
  onRateLimit?: (info: RateLimitInfo) => void,
): Promise<ConvertResponse> {
  const res = await fetch(`${API_URL}/api/convert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileId, outputFormat }),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Conversion request failed");
  }

  const data = await res.json();

  // Extract rate limit from headers first, fall back to response body
  if (onRateLimit) {
    const headerRateLimit = extractRateLimitFromFetch(res);
    if (headerRateLimit) {
      onRateLimit(headerRateLimit);
    } else if (data.remainingConversions !== undefined && data.dailyLimit !== undefined) {
      onRateLimit({ remaining: data.remainingConversions, limit: data.dailyLimit });
    }
  }

  return data;
}

export async function checkStatus(jobId: string): Promise<StatusResponse> {
  const res = await fetch(`${API_URL}/api/status/${jobId}`);

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Status check failed");
  }

  return res.json();
}

export async function fetchStats(): Promise<{ totalConversions: number }> {
  const res = await fetch(`${API_URL}/api/stats`);
  if (!res.ok) return { totalConversions: 0 };
  return res.json();
}

export function getDownloadUrl(jobId: string): string {
  return `${API_URL}/api/download/${jobId}`;
}

/** Preview item returned from the preview endpoint */
export interface PreviewItem {
  quality: number;
  size: number;
  compressionRatio: number;
  data: string; // base64 data URL
}

export interface PreviewResponse {
  previews: PreviewItem[];
  requestedCount: number;
  returnedCount: number;
  plan: string;
}

/** SSE event data from /api/stream/:jobId */
export interface StreamEvent {
  status: string;
  progress: number;
  downloadUrl?: string;
  outputSize?: number;
  error?: string;
}

/**
 * Connect to SSE stream for async job progress.
 * Returns a cleanup function to close the connection.
 */
export function connectJobStream(
  jobId: string,
  onEvent: (event: StreamEvent) => void,
  onError: (error: Error) => void,
): () => void {
  const eventSource = new EventSource(`${API_URL}/api/stream/${jobId}`);

  eventSource.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data) as StreamEvent;
      onEvent(data);

      // Close connection on terminal states
      if (data.status === "completed" || data.status === "failed" || data.status === "not_found") {
        eventSource.close();
      }
    } catch {
      onError(new Error("Failed to parse SSE event"));
      eventSource.close();
    }
  };

  eventSource.onerror = () => {
    onError(new Error("SSE connection error"));
    eventSource.close();
  };

  return () => eventSource.close();
}

/** Preview request timeout (ms). Long enough for Cloud Run cold start (~30s) + 4 conversions. */
const PREVIEW_TIMEOUT_MS = 90_000;
/** Backoff delay before single retry on transient network errors (ms). */
const PREVIEW_RETRY_DELAY_MS = 1_000;

/** A network-level failure where retrying may help. Some browsers / runtimes
 *  emit `TimeoutError` from `AbortSignal.timeout(...)` in addition to the
 *  classic `TypeError: Failed to fetch` and the `AbortError` we raise from
 *  our own AbortController. Treat all three as retryable. */
function isTransientFetchError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.name === "TypeError" ||
    err.name === "AbortError" ||
    err.name === "TimeoutError"
  );
}

async function preview_fetchOnce(
  formData: FormData,
  signal: AbortSignal,
): Promise<PreviewResponse> {
  const res = await fetch(`${API_URL}/api/preview`, {
    method: "POST",
    body: formData,
    signal,
  });

  if (!res.ok) {
    let message = "Preview request failed";
    try {
      const error = (await res.json()) as { message?: string };
      if (error?.message) message = error.message;
    } catch {
      // body was not JSON; keep default message
    }
    throw new Error(message);
  }

  return res.json();
}

export async function requestPreview(
  file: File,
  outputFormat: string,
  qualities: number[],
  plan: string,
): Promise<PreviewResponse> {
  const buildFormData = () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("outputFormat", outputFormat);
    fd.append("qualities", JSON.stringify(qualities));
    fd.append("plan", plan);
    return fd;
  };

  // Use a single absolute deadline so retry never extends UX latency past the
  // total budget. Without this, the worst case is 90s (attempt 1 timeout) +
  // 1s (backoff) + 90s (attempt 2 timeout) ≈ 181s.
  const deadline = Date.now() + PREVIEW_TIMEOUT_MS;

  const attempt = async () => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      throw new Error("Preview request timed out");
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), remaining);
    try {
      return await preview_fetchOnce(buildFormData(), controller.signal);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    return await attempt();
  } catch (err) {
    if (!isTransientFetchError(err)) throw err;
    if (Date.now() + PREVIEW_RETRY_DELAY_MS >= deadline) throw err;
    await new Promise((r) => setTimeout(r, PREVIEW_RETRY_DELAY_MS));
    return attempt();
  }
}
